import React, { useState } from "react";
import { useForm } from "react-hook-form";

import useAuth from "../../utils/hooks/useAuth";

import SmashLoader from "../../components/Loader/SmashLoader";
import DarkModeToggle from "../../components/Navigation/DarkModeToggle";
import { ReactComponent as SmashLogo } from "../../assets/smash_logo.svg";

export const ForgotPassword = () => {
  const { passwordResetToken } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [verified, setVerified] = useState(false);

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const success = await passwordResetToken(data.email);

      if (!success.success) {
        setError("email", {
          type: "manual",
          message: "Failed to send reset email. Try again.",
        });
        return;
      }

      setEmail(data.email);
      setVerified(true);
    } catch (error) {
      setError("email", {
        type: "manual",
        message: "Something went wrong. Please try again later.",
      });
      console.error("Reset request error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <SmashLoader />;
  }

  return (
    <div className="flex flex-row items-start justify-center w-full min-h-screen bg-blue-50 dark:bg-slate-900 p-6 overflow-y-auto">
      {/* Dark Mode Toggle */}
      <div className="fixed bottom-6 right-6 z-50">
        <DarkModeToggle />
      </div>
      <div className="flex flex-col justify-start flex-grow items-center w-full h-full gap-8">
        <div className="w-full flex flex-row justify-start items-center">
          <SmashLogo className="w-20 h-auto" />
        </div>
        {!verified ? (
          <div className="flex flex-col items-center justify-center flex-grow w-full h-full">
            <div className="p-10 bg-white dark:bg-slate-800 rounded-2xl shadow-lg flex flex-col items-center gap-8 w-full max-w-md">
              <div className="flex flex-col gap-2 w-full">
                <div className="text-center text-zinc-800 dark:text-zinc-200 text-3xl font-medium font-poppins ">
                  Forgot Password?
                </div>
                <div className="text-center text-stone-500 dark:text-stone-400 text-opacity-80 text-sm">
                  Please enter the email you want to retrieve the password for
                </div>
              </div>

              <form
                className="flex flex-col items-center gap-4 w-full"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="flex flex-col w-full gap-1">
                  <label className="text-gray-900 dark:text-gray-200 font-semibold text-sm mb-1">
                    Email<span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="w-full px-3 py-2 border rounded-lg dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs">
                      {errors.email.message}
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full h-12 bg-blue-950 dark:bg-blue-700 text-white rounded-xl hover:opacity-80"
                >
                  Next
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center flex-grow w-full">
            <div className="p-10 bg-white dark:bg-slate-800 rounded-2xl shadow-lg flex flex-col items-center gap-6 w-full max-w-md">
              <div className="text-center text-zinc-800 dark:text-zinc-200 text-3xl font-medium font-poppins">
                Forgot Password?
              </div>
              <div className="text-stone-500 dark:text-stone-400 text-opacity-80 text-lg text-center">
                Please check your email (
                <span className="font-semibold">{email}</span>)
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
